import { useCallback, useReducer } from 'react';
import { useMounted } from '../../../hooks/useMounted';
import { TableState } from '../TableState';
export const useTableState = tablePreference => {
  const [tableState, dispatch] = useReducer(TableState.reduce, TableState.init(tablePreference?.selectedRows, tablePreference?.selectAll, undefined, tablePreference?.frozenColumnCount ?? 0, tablePreference?.orderedColumnKeys, tablePreference?.resizedColumns, undefined, tablePreference?.scrollPosition));
  const mounted = useMounted();

  /**
   * Select a single row with an option to make a range selections
   * @param index the index of the row that is being selected or unselected
   * @param selected determine if the row should be selected or unselected
   * @param rowCount the total number of rows displayed on the table
   * @param selectType determine how the selection should behave
   * @param clearPrevious clears previous selection when set to false
   * @param rangeSelection selection will be treated as a range selection by not clearning previous selection
   * @param rowKey callback function that returns the rowKey for a given index
   * @param getRowIndex callback function that returns the index given a rowKey
   * @param hideSelectAll set to true if the select all checkbox is being hidden
   * @param areRowsVisible callback function that is expecting to return true or false based on the given selected keys
   * currently visbile or not
   * @param callback callback function that gets called when selections are being made
   * @param isRowDisabled Callback whether a row should be disabled or not.
   * @param isRowReadOnly Callback whether a row should be read-only or not.
   * @param selectionStart to explicity state the start index.
   */
  const setSelectRow = useCallback(props => {
    mounted.current && dispatch(TableState.actions.createSelectRow({
      ...props,
      interactive: props.interactive ?? false,
      rowCount: props.rowCount ?? -1,
      hideSelectAll: props.hideSelectAll
    }));
  }, [mounted]);

  /**
   * Select one or more rows
   * @param selectedRows array of row keys to be selected
   * @param rowCount the total number of rows displayed on the table
   * @param areRowsVisible callback function that is expecting to return true or false based on the given selected keys
   * currently visbile or not
   * @param callback callback function that gets called when selections are being made
   * @param isRowDisabled Callback whether a row should be disabled or not.
   * @param isRowReadOnly Callback whether a row should be read-only or not.
   */

  const setSelectRows = useCallback(props => {
    mounted.current && dispatch(TableState.actions.createSelectedRows({
      ...props,
      interactive: props.interactive ?? false,
      rowCount: props.rowCount ?? -1
    }));
  }, [mounted]);

  /**
   * Select or deselect all rows
   * @param selectAll determines if all rows should be selected or deselected
   * @param callback callback function that gets called when selections are being made
   */
  const setSelectAllRows = useCallback(props => {
    mounted.current && dispatch(TableState.actions.createSelectAll({
      ...props
    }));
  }, [mounted]);
  const setFreezeColumn = useCallback(props => {
    mounted.current && dispatch(TableState.actions.createFreezeColumn({
      ...props
    }));
  }, [mounted]);
  const setFreezeColumnCount = useCallback(props => {
    mounted.current && dispatch(TableState.actions.createFrozenColumnCount({
      ...props
    }));
  }, [mounted]);
  const setResizedColumn = useCallback(props => {
    mounted.current && dispatch(TableState.actions.createResizeColumn({
      ...props
    }));
  }, [mounted]);
  const setOrderedColumnKeys = useCallback(props => {
    mounted.current && dispatch(TableState.actions.createOrderedColumnKeys({
      ...props
    }));
  }, [mounted]);
  const setPartiallySelectedRows = useCallback(props => {
    mounted.current && dispatch(TableState.actions.createPartiallySelectedRows({
      partiallySelected: props
    }));
  }, [mounted]);
  const scrollTo = useCallback(props => {
    mounted.current && dispatch(TableState.actions.createScrollPosition({
      ...props
    }));
  }, [mounted]);

  /**
   * Clears cached measurements so the rowHeights and other measurement callbacks will called and recomputed when
   * the table re-renders. Eg. rowHeights will be called again with a re-render
   * @param startColumnIndex resets column measurement that are rendered after this index
   * @param startRowIndex resets row measurement cache that is rendered after this index
   */
  const clearMeasurementCache = useCallback(props => {
    mounted.current && dispatch(TableState.actions.createClearMeasurementCache({
      ...props
    }));
  }, [mounted]);
  const preference = {
    ...tablePreference,
    selectedRows: tableState.selectedRows,
    partiallySelectedRows: tableState.partiallySelectedRows,
    selectAll: tableState.selectAll,
    frozenColumnCount: tableState.frozenColumnCount,
    resizedColumns: tableState.resizedColumns,
    orderedColumnKeys: tableState.orderedColumnKeys,
    scrollPosition: tableState.scrollPosition,
    clearMeasurementsStartingColumnIndex: tableState.clearMeasurementsStartingColumnIndex,
    clearMeasurementsStartingRowIndex: tableState.clearMeasurementsStartingRowIndex,
    selectionMetadata: tableState.selectionMetadata,
    selectionAnchor: tableState.selectionAnchor
  };
  return {
    preference,
    dispatch,
    setSelectRow,
    setSelectRows,
    setSelectAllRows,
    setFreezeColumn,
    setOrderedColumnKeys,
    setResizedColumn,
    setFreezeColumnCount,
    setPartiallySelectedRows,
    scrollTo,
    clearMeasurementCache
  };
};
//# sourceMappingURL=useTableState.js.map